import { httpClient } from './httpclient';

/**
 * Função para cancelar uma venda
 * @param {string} saleId - ID da venda a ser cancelada
 * @param {string} reason - Motivo do cancelamento
 * @returns {Promise} - Resposta da API
 */
export const cancelSale = async (saleId, reason) => {
    try {
        const response = await httpClient.post(`/sales/cancel/${saleId}`, { reason: reason });
        return response;
    } catch (error) {
        console.error('Erro ao cancelar venda:', error);
        throw error;
    }
};

/**
 * Hook personalizado para gerenciar o cancelamento de venda
 * @param {function} onSuccess - Callback executado em caso de sucesso
 * @param {function} onError - Callback executado em caso de erro
 * @returns {object} - Objeto com funções e estados para cancelamento
 */
export const useSaleCancellation = (onSuccess, onError) => {
    const handleCancelSale = async (saleId, reason) => {
        try {
            const response = await cancelSale(saleId, reason);
            if (onSuccess) {
                onSuccess(response);
            }
            return response;
        } catch (error) {
            if (onError) {
                onError(error);
            }
            throw error;
        }
    };

    return {
        cancelSale: handleCancelSale
    };
};

function pickFirst(row, keys) {
    if (!row || typeof row !== 'object') return undefined;
    for (let i = 0; i < keys.length; i++) {
        const v = row[keys[i]];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return undefined;
}

/**
 * Coluna "Valor Total" (listagem / relatório): total da venda antes do desconto em itens no payload do chart.
 * API: `total`; fallback para campos antigos.
 */
export const getSaleRowGrossAmount = (row) => {
    return pickFirst(row, [
        'total',
        'Total',
        'items_gross_subtotal',
        'ItemsGrossSubtotal',
        'itemsGrossSubtotal',
        'total_amount',
        'TotalAmount',
    ]) ?? '0,00';
};

/**
 * Coluna "Desc. itens": soma do desconto nas linhas.
 * API: `item_discount_total`; fallback `total_item_discount`.
 */
export const getSaleRowItemDiscount = (row) => {
    return pickFirst(row, [
        'item_discount_total',
        'ItemDiscountTotal',
        'itemDiscountTotal',
        'total_item_discount',
        'TotalItemDiscount',
        'totalItemDiscount',
    ]) ?? '0,00';
};

/**
 * Coluna "Total c/ Desconto": total líquido da venda (listagem / relatório).
 * API: `total_discount`; fallbacks apenas se o campo não vier no payload.
 */
export const getSaleRowFinalTotal = (row) => {
    return pickFirst(row, [
        'total_discount',
        'TotalDiscount',
        'total_with_discount',
        'TotalWithDiscount',
        'final_total',
        'FinalTotal',
        'finalTotal',
    ]) ?? '0,00';
};

/**
 * Valor final no resumo da venda (GET /sales/{id}/resume).
 * `total_discount` no resume é o total líquido após desconto na venda (como em Sale.js);
 * quando não há desconto na venda vem "0,00" — nesse caso o total correto está em
 * `total_amount` / `items_gross_subtotal` ou em `net_after_item_discounts` (desconto só em itens).
 * `final_total` vazio ("") deve ser tratado como ausente — `??` não ignora string vazia.
 */
export const getSaleResumeFinalAmount = (sale) => {
    if (!sale || typeof sale !== 'object') return '0,00';

    const isBlank = (v) => v === undefined || v === null || v === '';
    const isZeroMoney = (v) => {
        if (v === 0) return true;
        if (typeof v !== 'string') return false;
        const n = parseFloat(String(v).replace(/\./g, '').replace(',', '.'));
        return !Number.isNaN(n) && n === 0;
    };

    if (!isBlank(sale.final_total)) return sale.final_total;

    // Remainder após desconto na venda; "0,00" = sem desconto global (usar outros campos).
    if (!isBlank(sale.total_discount) && !isZeroMoney(sale.total_discount)) {
        return sale.total_discount;
    }

    if (!isBlank(sale.net_after_item_discounts)) return sale.net_after_item_discounts;

    const fallback = pickFirst(sale, ['total_amount', 'items_gross_subtotal']);
    return fallback !== undefined ? fallback : '0,00';
};

/**
 * Indica se há desconto em produtos (linhas) no resumo da venda.
 * Usado para exibir "Líquido após itens" apenas quando o desconto não é R$ 0,00.
 */
export const hasNonZeroProductItemDiscount = (sale) => {
    if (!sale || typeof sale !== 'object') return false;
    const d = sale.total_item_discount;
    if (d === undefined || d === null || d === '') return false;
    if (d === 0) return false;
    if (typeof d === 'number') return d !== 0;
    const n = parseFloat(String(d).replace(/\./g, '').replace(',', '.'));
    return !Number.isNaN(n) && n !== 0;
};
